/* eslint-disable max-statements */
import { UseFormReturnType } from '@mantine/form';
import { cloneDeep } from 'lodash';
import { useMemo } from 'react';

import { Declaration, DeclarationDocumentsEnum, DeclarationHighValueGroupType, Documents, EnvironmentCountries, TransactionTypes } from '@e-origin/shared';

import { DeclarationDocumentList, DocumentChangeProps, getDeclarationDocumentById, getDeclarationDocumentPos } from '@components';
import { MutateDeclarationDocumentOperation } from '@hooks/declaration';
import { useSettings } from '@hooks/useSettings';
import { isHighValueDeclaration } from '@utils';
import { BeforeSendingFormData } from './beforeSendingBatchFormIntializer';

interface FinalizeDeclarationDocumentsProps {
  form: UseFormReturnType<BeforeSendingFormData>;
  declaration: Declaration;
  disabled: boolean;
}
export const FinalizeDeclarationDocuments: React.FC<FinalizeDeclarationDocumentsProps> = (props) => {
  const { form, declaration } = props;
  const { data: settings } = useSettings();

  const declarationGroup = declaration?.generalInfo?.group as DeclarationHighValueGroupType;

  const handleDocuments = (doc: DocumentChangeProps) => {
    const refreshedDocuments = cloneDeep(form.values.documents);

    switch (doc.operation) {
      case MutateDeclarationDocumentOperation.CREATE: {
        refreshedDocuments[doc.type] = [...(refreshedDocuments[doc.type] || []), doc.data];
        break;
      }
      case MutateDeclarationDocumentOperation.UPDATE: {
        const documentToUpdate = getDeclarationDocumentById(doc.id, refreshedDocuments[doc.type]);
        const documentPos = refreshedDocuments[doc.type].findIndex((d) => d === documentToUpdate);
        refreshedDocuments[doc.type][documentPos] = doc.data;
        break;
      }
      case MutateDeclarationDocumentOperation.DELETE: {
        const index = getDeclarationDocumentPos(doc.data, doc.type, refreshedDocuments[doc.type]);

        if (index !== -1) {
          refreshedDocuments[doc.type].splice(index, 1);
        }
        break;
      }
      default:
        break;
    }

    form.setFieldValue('documents', refreshedDocuments);
    form.setFieldValue('warehouse', refreshedDocuments.warehouseDocuments?.[0]?.identifier || '');
  };

  const excludeDocuments = useMemo(() => {
    const excluded = [];

    if (declaration.transactionType === TransactionTypes.B2C_HV_BEGATE) {
      excluded.push(DeclarationDocumentsEnum.PREVIOUS_DOCUMENTS);
    }

    if (declarationGroup === DeclarationHighValueGroupType.H2) {
      excluded.push(DeclarationDocumentsEnum.ADDITIONS_AND_DEDUCTIONS, DeclarationDocumentsEnum.ADDITIONAL_FISCAL_REFERENCE);
      if (settings.country === EnvironmentCountries.NL) {
        excluded.push(DeclarationDocumentsEnum.GUARANTEE_DOCUMENTS);
      }
    }

    return excluded;
  }, [declaration.transactionType, declarationGroup, settings.country]);

  return (
    <DeclarationDocumentList
      documents={form.values.documents as Documents}
      editDisabled={props.disabled}
      includeDocumentTypes={
        declaration.transactionType === TransactionTypes.B2B_MULTIPLE
          ? [DeclarationDocumentsEnum.PREVIOUS_DOCUMENTS, DeclarationDocumentsEnum.ADDITIONAL_INFORMATION, DeclarationDocumentsEnum.WAREHOUSE_DOCUMENT]
          : undefined
      }
      excludeDocumentTypes={declaration.transactionType !== TransactionTypes.B2B_MULTIPLE ? excludeDocuments : undefined}
      onDocumentChange={handleDocuments}
      defaultHeaderValue={true}
      headerDisabled={true}
      isHighValue={isHighValueDeclaration(declarationGroup)}
      declaration={declaration}
      data-pw-prefix="finalize-batch"
    />
  );
};
